import {
  EnterpriseRatingSeverityDefault,
  IUnderwritingCoverage,
  IUnderwritingEndorsement,
  SignificantTermsConditions,
  SubmissionType,
} from '@converge/graphql';
import {
  IEnterpriseRatingRiskLevelInput,
  IRaterContextState,
  IScheduleRatingInput,
  IUnderwritingSocketCoverageInput,
  IUnderwritingSocketEndorsementInput,
  IUnderwritingSocketExcessInput,
  IUnderwritingSocketExcessParticipantInput,
  IUnderwritingSocketQuoteInput,
  IUnderwritingSocketSubCoverageInput,
} from '../types';
import {
  getAggregateLimitFromQuote,
  getRetentionFromQuote,
} from './rater.reducer';

import { isPrimary } from '../../../entity';

export const createRaterRequestMessage = (
  state: IRaterContextState
): IUnderwritingSocketQuoteInput => {
  const insuredEntity = {
    primaryIndustrySector:
      state.submission.insuredEntity?.node.industrySectors.edges.find((e) =>
        isPrimary(e.node)
      )?.node,
    primaryFundamentals:
      state.submission.insuredEntity?.node.fundamentals.edges.find((e) =>
        isPrimary(e.node)
      )?.node,
  };

  const policyRetention =
    getRetentionFromQuote(state.quote) ??
    state.quote.retention ??
    state.submission.policyRetention ??
    -1;

  const rootEndorsements: IUnderwritingSocketEndorsementInput[] = [];
  const coverageEndorsements: {
    [key: string]: IUnderwritingSocketEndorsementInput[];
  } = {};
  state.quote.endorsements.forEach((endorsement) => {
    const endOBJ = {
      uuid: endorsement.uuid ?? 'n/a',
      included: endorsement.quote.included === true,
      nonPremiumBearing: endorsement.nonPremiumBearing,
      limit: endorsement.nonPremiumBearing
        ? null
        : endorsement.quote.limit ?? null,
      retention: endorsement.nonPremiumBearing
        ? null
        : endorsement.quote.retention ?? null,
      fusionScore: endorsement.nonPremiumBearing
        ? null
        : endorsement.quote.fusionScore ?? 0,
      selectedFusionScoreModifier: endorsement.nonPremiumBearing
        ? null
        : endorsement.quote.selectedFusionScoreModifier ?? null,
      // TODO: Confirm: Should we support this for endorsements?
      // restorationPeriodDays:
      //   endorsement.quote.restorationPeriodDays ?? undefined,
      // TODO: Confirm: Should we support this for endorsements?
      // waitingPeriodHours: endorsement.quote.waitingPeriodHours ?? undefined,
      mediaEventCoverageOption: endorsement?.features?.enableMediaEventCoverage
        ? endorsement.quote.mediaEventCoverageOption ?? undefined
        : undefined,
      ...getFullPriorActsAndRetroactiveDateFields(endorsement),
    };

    if (!endorsement.coverageUuid) {
      rootEndorsements.push(endOBJ);
    } else {
      if (coverageEndorsements[endorsement.coverageUuid] === undefined) {
        coverageEndorsements[endorsement.coverageUuid] = [];
      }
      coverageEndorsements[endorsement.coverageUuid].push(endOBJ);
    }
  });

  const message: IUnderwritingSocketQuoteInput = {
    policyType: state.submission.type ?? SubmissionType.Primary,
    endorsements: rootEndorsements,
    coverages: state.quote.coverages.map(
      (coverage): IUnderwritingSocketCoverageInput => {
        return {
          uuid: coverage.uuid,
          endorsements: coverageEndorsements[coverage.uuid] ?? [],
          fusionScore: coverage.quote?.fusionScore ?? 0,
          selectedFusionScoreModifier:
            coverage.quote?.selectedFusionScoreModifier ?? null,
          limit: coverage.quote?.limit ?? 0,
          retention: coverage.quote?.retention ?? 0,
          included: coverage.quote?.included ?? false,
          waitingPeriodDays: coverage.features.enableWaitingPeriodDays
            ? coverage.quote?.waitingPeriodDays ?? undefined
            : undefined,
          waitingPeriodHours: coverage.features.enableWaitingPeriodHours
            ? coverage.quote?.waitingPeriodHours ?? undefined
            : undefined,
          restorationPeriodDays: coverage.features.enableRestorationPeriodDays
            ? coverage.quote?.restorationPeriodDays ?? undefined
            : undefined,
          subCoverages: coverage.subCoverages.length
            ? coverage.subCoverages.map(
                (subCoverage): IUnderwritingSocketSubCoverageInput => {
                  return {
                    // TODO: Fix me and assess if a null or a 0 is more appropriate than -1:
                    subLimit: subCoverage.quote?.subLimit ?? -1,
                    // TODO: Fix me and assess if a null or a 0 is more appropriate than -1:
                    subRetention: subCoverage.quote?.subRetention ?? -1,
                    included: subCoverage.quote?.included === true,
                    uuid: subCoverage.uuid ?? '',
                    waitingPeriodDays:
                      subCoverage.quote?.waitingPeriodDays ?? undefined,
                    waitingPeriodHours:
                      subCoverage.quote?.waitingPeriodHours ?? undefined,
                    restorationPeriodDays:
                      subCoverage.quote?.restorationPeriodDays ?? undefined,
                  };
                }
              )
            : undefined,
          ...getFullPriorActsAndRetroactiveDateFields(coverage),
        };
      }
    ),
    endorsementImpact:
      state.quote.endorsementImpact ?? SignificantTermsConditions.Neutral,
    enterpriseRating: {
      selectedFusionScoreModifier:
        state.quote.erSelectedFusionScoreModifier ?? null,
      // TODO: Use quote's enterprise rating levels when the query/mutation is updated
      // riskLevels: state.quote.enterpriseRatingRiskLevels ?? [],
      riskLevels: (state.quote.enterpriseRatings ?? []).map(
        (data): IEnterpriseRatingRiskLevelInput => {
          return {
            riskLevel: data.quote.riskLevel ?? EnterpriseRatingSeverityDefault,
            uuid: data.uuid,
          };
        }
      ),
    },
    scheduleRatings: (state.quote.scheduleRatings ?? [])
      .filter((scheduleRating) => scheduleRating.primary)
      .map((scheduleRating): IScheduleRatingInput => {
        return {
          uuid: scheduleRating.uuid,
          percentage:
            typeof scheduleRating.quote?.percentage === 'number'
              ? scheduleRating.quote?.percentage
              : undefined,
        };
      }),
    selectedQuotedPremium: state.quote.selectedQuotedPremium ?? null,
    quotedRatedRatio: state.quote.quotedRatedRatio,
    stcSelectedModifier: state.quote.stcSelectedModifier ?? null,
    naicsCode: insuredEntity.primaryIndustrySector?.naicsCode ?? -1,
    programUuid: state.submission.program?.node.uuid,
    policyRetention,
    aggregateLimit:
      getAggregateLimitFromQuote(state.quote) ??
      state.quote.limit ??
      state.submission.aggregateLimit ??
      -1,
    revenue: insuredEntity.primaryFundamentals?.ratedRevenue ?? -1,
    policyStartDate: state.submission.policyStartDate ?? '',
    policyEndDate: state.submission.policyEndDate ?? '',
    retroactiveDate: state.submission.policyStartDate ?? '',
    brokerCommission: state.submission.brokerCommission ?? 0,
    coverageLevelScoring: state.submission.coverageLevelScoring ?? false,
    submissionCreatedAt: state.submission.createdAt ?? null,
  };

  if (state.submission.type === SubmissionType.Excess) {
    message.excess = {
      endorsementImpact:
        state.quote.excessEndorsementImpact ??
        SignificantTermsConditions.Neutral,
      scheduleRatings: state.quote.scheduleRatings
        .filter((scheduleRating) => scheduleRating.excess)
        .map((scheduleRating): IScheduleRatingInput => {
          return {
            uuid: scheduleRating.uuid,
            percentage:
              typeof scheduleRating.quote?.percentage === 'number'
                ? scheduleRating.quote?.percentage / 100
                : undefined,
          };
        }),
      selectedCorrectiveFactor:
        state.quote.excessSelectedCorrectiveFactor ??
        state.quote.excessSelectedCorrectiveFactorNew ??
        null,
      stcSelectedModifier: state.quote.stcExcessSelectedModifier ?? null,
      tower: (state.quote.excessTowerLayers ?? []).map(
        (
          { boundPremium, ...layer },
          index
        ): IUnderwritingSocketExcessInput['tower'][0] => {
          return {
            position: layer.position ?? index,
            premium: boundPremium || null,
            limit: (layer.limit ?? 0) === 0 ? null : layer.limit ?? null,
            retention: layer.primary ? policyRetention : undefined,
            participants: layer.participants.map(
              (participant): IUnderwritingSocketExcessParticipantInput => {
                return {
                  limit:
                    (participant.quote?.limit ?? 0) === 0
                      ? null
                      : participant.quote?.limit ?? null,
                  uuid: participant.uuid ?? '',
                };
              }
            ),
          };
        }
      ),
    };
  }

  return message;
};

const getFullPriorActsAndRetroactiveDateFields = (
  lineItem: Pick<
    IUnderwritingCoverage | IUnderwritingEndorsement,
    'features'
  > & {
    quote: Pick<
      (IUnderwritingCoverage | IUnderwritingEndorsement)['quote'],
      'fullPriorActs' | 'retroactiveDate'
    >;
  }
): {
  fullPriorActs?: boolean;
  retroactiveDate?: string;
} => {
  return {
    fullPriorActs: lineItem?.features?.enableRetroactiveDateOrFullPriorActs
      ? lineItem.quote?.fullPriorActs ?? false
      : undefined,
    retroactiveDate: lineItem?.features?.enableRetroactiveDateOrFullPriorActs
      ? lineItem.quote?.retroactiveDate ?? undefined
      : undefined,
  };
};
